import { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { RootState } from '../../../reducers';
import { FileType } from '../../../types';
import { screenAppAPIClient } from '../../../lib/baseAxios';

// Types for the backend response
interface AssistantSearchResponse {
  searchResults: FileType[];
  aiAnswer: string;
  featuredThumbnail?: string;
  thumbnails?: Record<string, string>;
  processingTime: number;
  queryOptimization?: {
    originalQuery: string;
    optimizedQuery: string;
  };
  aiAnswerKey?: string;
  isAiAnswerPending?: boolean;
}

interface AssistantSearchRequest {
  query: string;
  includeThumbnails?: boolean;
  limit?: number;
  context?: {
    currentFileId?: string;
    userSettings?: {
      name?: string;
      role?: string;
      teamName?: string;
      customVocabulary?: string;
      defaultLanguage?: string;
    };
  };
}

interface UseBackendAssistantSearchReturn {
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  results: AssistantSearchResponse | null;
  error: string | null;
  isGeneratingAnswer: boolean;
  performSearch: (
    query: string,
    context?: AssistantSearchRequest['context'],
  ) => Promise<void>;
  reset: () => void;
  searchResults: FileType[]; // Immediate search results
  isSearchResultsReady: boolean; // Whether search results are available
}

const useBackendAssistantSearch = (): UseBackendAssistantSearchReturn => {
  const [status, setStatus] = useState<
    'idle' | 'loading' | 'succeeded' | 'failed'
  >('idle');
  const [results, setResults] = useState<AssistantSearchResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isGeneratingAnswer, setIsGeneratingAnswer] = useState(false);
  const [searchResults, setSearchResults] = useState<FileType[]>([]);
  const [isSearchResultsReady, setIsSearchResultsReady] = useState(false);
  const [pollingTimeoutId, setPollingTimeoutId] = useState<number | null>(null);

  // Get user and team info from Redux
  const userSettings = useSelector((state: RootState) => state.settings.user);
  const teamState = useSelector((state: RootState) => state.team.selectedTeam);
  const recorderSettings = useSelector(
    (state: RootState) => state.settings.recorder,
  );
  const selectedVideo = useSelector(
    (state: RootState) => state.selectedVideo?.file,
  );

  // Get URL parameters
  const { teamId } = useParams<{
    teamId?: string;
  }>();

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingTimeoutId) {
        window.clearTimeout(pollingTimeoutId);
      }
    };
  }, [pollingTimeoutId]);

  // Function to poll for AI answer
  const pollForAiAnswer = useCallback(
    async (aiAnswerKey: string) => {
      if (!teamId) return;

      try {
        const response = await (screenAppAPIClient as any).api.get(
          `/v2/files/assistant-answer/${teamId}/${aiAnswerKey}`,
        );

        if (response.data.success) {
          // Update the results with the AI answer
          setResults((prevResults) => {
            if (prevResults) {
              return {
                ...prevResults,
                aiAnswer: response.data.data.aiAnswer,
                isAiAnswerPending: false,
              };
            }
            return prevResults;
          });
          setIsGeneratingAnswer(false);
        } else {
          // If answer is not ready yet, schedule another poll
          const timeoutId = window.setTimeout(
            () => pollForAiAnswer(aiAnswerKey),
            2000,
          );
          setPollingTimeoutId(timeoutId);
        }
      } catch (err: any) {
        if (err.response?.status === 404) {
          // Answer not found, might be still generating, continue polling
          const timeoutId = window.setTimeout(
            () => pollForAiAnswer(aiAnswerKey),
            2000,
          );
          setPollingTimeoutId(timeoutId);
        } else {
          console.error('Error polling for AI answer:', err);
          setIsGeneratingAnswer(false);
        }
      }
    },
    [teamId],
  );

  const performSearch = useCallback(
    async (query: string, context?: AssistantSearchRequest['context']) => {
      if (!query.trim() || !teamId) {
        setError('Query and team ID are required');
        return;
      }

      setStatus('loading');
      setError(null);
      setIsGeneratingAnswer(false);
      setSearchResults([]);
      setIsSearchResultsReady(false);

      // Clear any existing polling
      if (pollingTimeoutId) {
        window.clearTimeout(pollingTimeoutId);
        setPollingTimeoutId(null);
      }

      const requestBody: AssistantSearchRequest = {
        query: query.trim(),
        includeThumbnails: true,
        limit: 15, // limit is the number of files to return
        context: {
          currentFileId: selectedVideo?._id,
          userSettings: {
            name: userSettings?.name,
            role: userSettings?.role,
            teamName: teamState?.name,
            customVocabulary: teamState?.fileOptions?.customVocabulary,
            defaultLanguage:
              recorderSettings?.otherSettings?.defaultTranscriptionLanguage,
          },
          ...context,
        },
      };

      try {
        // Use the screenAppAPIClient's internal API instance
        const response = await (screenAppAPIClient as any).api.post(
          `/v2/files/assistant-search/${teamId}`,
          requestBody,
        );

        if (response.data.success) {
          const responseData = response.data.data;

          // Immediately set search results (available right away)
          setSearchResults(responseData.searchResults || []);
          setIsSearchResultsReady(true);
          setResults(responseData);
          setStatus('succeeded');

          // Handle AI answer generation
          if (responseData.isAiAnswerPending && responseData.aiAnswerKey) {
            // AI answer is being generated in background, start polling
            setIsGeneratingAnswer(true);
            const timeoutId = window.setTimeout(
              () => pollForAiAnswer(responseData.aiAnswerKey!),
              1000,
            );
            setPollingTimeoutId(timeoutId);
          } else {
            // AI answer is available immediately or no AI processing needed
            setIsGeneratingAnswer(false);
          }
        } else {
          setError(response.data.message || 'Search failed');
          setStatus('failed');
        }
      } catch (err: any) {
        console.error('Assistant search error:', {
          error: err,
          message: err.message,
          response: err.response,
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          requestBody,
          teamId,
          url: `/v2/files/assistant-search/${teamId}`,
          baseURL: (screenAppAPIClient as any).api.defaults.baseURL,
        });

        // Provide more detailed error messages
        let errorMessage = 'Search failed';

        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        }

        setError(errorMessage);
        setStatus('failed');
      }
    },
    [
      teamId,
      selectedVideo,
      userSettings,
      teamState,
      recorderSettings,
      pollForAiAnswer,
      pollingTimeoutId,
    ],
  );

  const reset = useCallback(() => {
    setStatus('idle');
    setResults(null);
    setError(null);
    setIsGeneratingAnswer(false);
    setSearchResults([]);
    setIsSearchResultsReady(false);

    // Clear any existing polling
    if (pollingTimeoutId) {
      clearTimeout(pollingTimeoutId);
      setPollingTimeoutId(null);
    }
  }, [pollingTimeoutId]);

  // Clear generating state when we receive an AI answer
  useEffect(() => {
    if (results?.aiAnswer && isGeneratingAnswer) {
      setIsGeneratingAnswer(false);
    }
  }, [results?.aiAnswer, isGeneratingAnswer]);

  return {
    status,
    results,
    error,
    isGeneratingAnswer,
    performSearch,
    reset,
    searchResults,
    isSearchResultsReady,
  };
};

export default useBackendAssistantSearch;
